import './Selects.css'

import { memo, useEffect, useRef, useState } from 'react'

import { AngleIcon } from '../../../components/Icons'
import { useClickOutside } from '../../../hooks/useClickOutside'

export interface ICustomSelectProps {
  options: string[]
  selectedOption: string
  disabled?: boolean
  onSelectChange: (name: string, value: string) => void
  name: string
  clearable?: boolean
}

const CustomSelect: React.FC<ICustomSelectProps> = memo(
  ({
    options,
    selectedOption,
    disabled,
    name,
    onSelectChange,
    clearable = true,
  }) => {
    const containerRef = useRef(null)
    const [showOptions, setShowOptions] = useState(false)
    const [selected, setSelected] = useState<string>(selectedOption)
    useClickOutside(containerRef, () => {
      setShowOptions(false)
    })

    useEffect(() => {
      if (selectedOption) {
        setSelected(selectedOption)
      }
    }, [selectedOption])

    const toggleOptions = () => {
      if (disabled) return
      setShowOptions(!showOptions)
    }

    const clearFilter = (
      e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    ) => {
      e.stopPropagation()
      setSelected(options[0])
      onSelectChange(name, '')
    }

    const handleClick = (option: string) => {
      setSelected(option)
      onSelectChange(name, option)
      setShowOptions(false)
    }

    return (
      <div ref={containerRef} className="relative">
        <div
          onClick={toggleOptions}
          className={`${disabled ? '' : 'cursor-pointer'} hover:bg-opacity-20 tc-mv-select-selectbox`}
        >
          <div className="tc-mv-select-selected-option truncate">
            {selected}
          </div>
          {!disabled && (
            <div className="flex gap-2 items-center">
              {!clearable || selected === options[0] ? null : (
                <button
                  onClick={clearFilter}
                  className="tc-mv-select-selectbox-arrow"
                >
                  <i className="fa-solid fa-xmark text-xl" />
                </button>
              )}
              <div className="tc-mv-select-selectbox-arrow">
                <AngleIcon />
              </div>
            </div>
          )}
        </div>
        {showOptions && (
          <div className="px-4 py-4 tc-mv-select-options">
            <div className="h-full w-full overflow-auto">
              {options.map(option => (
                <div
                  key={option}
                  className={`rounded-xl text-sm w-full break-words cursor-pointer hover:bg-black hover:bg-opacity-20 px-4 py-[10px] ${
                    selected === option ? 'bg-black bg-opacity-20' : ''
                  }`}
                  onClick={() => {
                    handleClick(option)
                  }}
                >
                  {option}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    )
  },
)

export { CustomSelect }
